export default class Form {
  constructor(container) {
    this.container = container;
  }

  init() {
    const observer = new MutationObserver((mutations, obs) => {
      const formElem = this.container.querySelector('form');
      if (formElem) {
        this.hideFieldsets(formElem);
        this.replaceSubmitInputWithButton(formElem);
        this.duplicateLabelSpanText(formElem);
        this.hideEmptyOptions(formElem);
        this.updateHeaderElements(formElem);
        this.updateSelectFirstOption(formElem);
        this.watchForErrors(formElem);
        this.updateSelectTextColor(formElem);
        this.updateDependentFields(formElem);
        obs.disconnect();
      }
    });

    observer.observe(this.container, { childList: true, subtree: true });
  }

  updateSelectFirstOption(formElem) {
    const handleSelectField = (field) => {
      const label = field.querySelector('label');
      const select = field.querySelector('select');

      if (!label) return;

      const formControl = field.querySelector(
        'select, input:not([type="hidden"]), textarea'
      );

      if (formControl?.id) {
        label.htmlFor = formControl.id;
      } else {
        label.remove();
        return;
      }

      if (!select) return;

      const labelSpan = label.querySelector('span:not(.hs-form-required)');
      const requiredSpan = label.querySelector('span.hs-form-required');

      let labelText = '';
      if (labelSpan) {
        labelText = labelSpan.innerHTML.trim();
      } else {
        labelText = label.innerHTML.trim();
      }

      // Add required asterisk if it exists
      if (requiredSpan && !labelText?.includes('*')) {
        labelText += requiredSpan.textContent;
      }

      const firstOption = select.querySelector('option');
      if (!firstOption) return;

      // Avoid duplicating logic if already initialized
      if (firstOption.dataset.initialized) return;
      firstOption.dataset.initialized = 'true';

      const originalOptionText = firstOption.textContent.trim();
      firstOption.dataset.originalOptionText = originalOptionText;
      firstOption.textContent = labelText;

      label.classList.add('hs-floating-label');

      select.addEventListener('focus', () => {
        label.style.setProperty('--label-opacity', '1');
        if (firstOption.dataset.originalOptionText) {
          firstOption.textContent = firstOption.dataset.originalOptionText;
        }
      });
    };

    // Handle existing fields on load
    const existingFields = formElem.querySelectorAll('.hs-fieldtype-select');
    existingFields.forEach(handleSelectField);

    // Observe for dynamically added select fields
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          if (node.matches('.hs-fieldtype-select')) {
            handleSelectField(node);
          } else {
            // In case the new node contains nested select fields
            const nestedFields = node.querySelectorAll?.('.hs-fieldtype-select');
            nestedFields?.forEach(handleSelectField);
          }
        });
      });
    });

    observer.observe(formElem, {
      childList: true,
      subtree: true
    });
  }

  updateSelectTextColor(formElem) {
    const handleSelect = (select) => {
      if (select.dataset.colorInitialized) return;
      select.dataset.colorInitialized = 'true';

      const updateColor = () => {
        const isPlaceholder = select.selectedIndex === 0 || !select.value;
        select.style.color = isPlaceholder ? '#335E80' : '#003661';
      };

      updateColor(); // Initial color
      select.addEventListener('change', updateColor);
    };

    // Handle existing selects on load
    const existingSelects = formElem.querySelectorAll('select');
    existingSelects.forEach(handleSelect);

    // Observe for dynamically added selects
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          if (node.tagName === 'SELECT') {
            handleSelect(node);
          } else {
            const nestedSelects = node.querySelectorAll?.('select');
            nestedSelects?.forEach(handleSelect);
          }
        });
      });
    });

    observer.observe(formElem, {
      childList: true,
      subtree: true
    });
  }

  hideFieldsets(formElem) {
    const fieldsets = formElem.querySelectorAll('fieldset');

    fieldsets.forEach(fieldset => {
      const hsField = fieldset.querySelector('.hs-form-field');
      if (hsField && window.getComputedStyle(hsField).display === 'none') {
        fieldset.style.display = 'none';
      }
    });
  }

  replaceSubmitInputWithButton(formElem) {
    const submitInput = formElem.querySelector('.hs-button[type="submit"]');
    if (submitInput && submitInput.tagName.toLowerCase() === 'input') {
      const button = document.createElement('button');

      for (const attr of submitInput.attributes) {
        if (attr.name !== 'type') {
          button.setAttribute(attr.name, attr.value);
        }
      }

      button.type = 'submit';
      button.classList.add('canvas-btn');
      button.setAttribute('data-skin', 'light');

      const text = submitInput.value || 'Submit';

      const textSpan = document.createElement('span');
      textSpan.className = 'canvas-btn__text';
      textSpan.textContent = text;

      const wrapperSpan = document.createElement('span');
      wrapperSpan.className = 'canvas-btn__text-wrapper';
      wrapperSpan.setAttribute('data-text', text);
      wrapperSpan.appendChild(textSpan);

      button.appendChild(wrapperSpan);
      submitInput.parentNode.replaceChild(button, submitInput);
    }
  }

  duplicateLabelSpanText(formElem) {
    const textFields = formElem.querySelectorAll('.hs-fieldtype-text, .hs-fieldtype-phonenumber, .hs-fieldtype-textarea');

    textFields.forEach(field => {
      const label = field.querySelector('label');
      const inputWrapper = field.querySelector('.input');
      const inputElement = field.querySelector(
        'input:not([type="hidden"]), textarea'
      );

      if (!label || !inputWrapper || inputWrapper.querySelector('span')) return;

      if (!inputElement) {
        label.remove();
        return;
      }

      if (inputElement.id) {
        label.htmlFor = inputElement.id;
      }

      const spans = label.querySelectorAll('span');
      if (spans.length === 0) return;

      const firstSpan = spans[0];
      const requiredSpan = label.querySelector('span.hs-form-required');

      const floatingLabel = document.createElement('span');
      floatingLabel.className = 'hs-floating-label';

      // Get the label text from the first span
      let labelText = firstSpan.innerHTML;

      // If there's a required asterisk, add it to the floating label
      if (requiredSpan && !labelText?.includes('*')) {
        labelText += requiredSpan.innerHTML;
      }

      if (requiredSpan && labelText?.includes('*')) {
        console.log(labelText);
        requiredSpan.remove();
      }

      floatingLabel.innerHTML = labelText;

      floatingLabel.style.setProperty('--floating-label-opacity', '1');
      firstSpan.style.setProperty('--label-opacity', '0');

      // (inputWrapper.querySelector('span')) ? inputWrapper.querySelector('span').remove() : null;
      inputWrapper.appendChild(floatingLabel)

      inputElement.addEventListener('focus', () => {
        floatingLabel.style.setProperty('--floating-label-opacity', '0');
        spans.forEach(span => {
          span.style.setProperty('--label-opacity', '1');
        });
      });

      inputElement.addEventListener('blur', () => {
        if (!inputElement.value.trim()) {
          floatingLabel.style.setProperty('--floating-label-opacity', '0');
        }
      });
    });
  }

  updateDependentFields(formElem) {
    const dependentFields = formElem.querySelectorAll('.hs-dependent-field');
    const dependentFieldsRadioButton = formElem.querySelectorAll('.hs-dependent-field input[type="radio"]');
    if (!dependentFields) return;

    dependentFieldsRadioButton.forEach(field => {
      field.addEventListener('change', () => {
        this.duplicateLabelSpanText(formElem);
      });
    });
  }

  hideEmptyOptions(formElem) {
    const selects = formElem.querySelectorAll('select');

    selects.forEach(select => {
      const options = select.querySelectorAll('option');
      options.forEach(option => {
        if (option.value === '') {
          option.hidden = true;
        }
      });
    });
  }

  watchForErrors(formElem) {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const field = mutation.target;
          const hasError = field.classList.contains('error');
          const fieldWrapper = field.closest('.hs-form-field');

          if (!fieldWrapper) return;

          const label = fieldWrapper.querySelector('label');
          const floatingLabel = fieldWrapper.querySelector('.hs-floating-label');

          if (label) {
            label.classList.toggle('error', hasError);
          }

          if (floatingLabel) {
            floatingLabel.classList.toggle('error', hasError);
          }
        }
      });
    });

    const fields = formElem.querySelectorAll('.hs-input');

    fields.forEach(field => {
      observer.observe(field, {
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  }

  updateHeaderElements(formElem) {
    // Update .form-header to h2 if not already
    const headers = formElem.querySelectorAll('.form-header');
    headers.forEach(header => {
      if (header.tagName.toLowerCase() !== 'h2') {
        const newEl = document.createElement('h2');
        newEl.className = header.className;
        newEl.innerHTML = header.innerHTML;
        newEl.classList.add('h3');
        header.replaceWith(newEl);
      }
    });

    // Update .form-subheader to h3 if not already
    const subheaders = formElem.querySelectorAll('.form-subheader');
    subheaders.forEach(subheader => {
      if (subheader.tagName.toLowerCase() !== 'h3') {
        const newEl = document.createElement('h3');
        newEl.className = subheader.className;
        newEl.innerHTML = subheader.innerHTML;
        newEl.classList.add('b3');
        subheader.replaceWith(newEl);
      }
    });
  }
}
